/**
 * modern_blue.js — Product Manager
 * ─────────────────────────────────────────────────────────────────────────────
 * Design Philosophy:
 *   Goal:         Product & Program leadership — showing metrics, execution, strategy
 *   Hierarchy:    Summary & Skills (sidebar scan) → Experience (impact metrics) → Projects
 *   Divider:      Left-bar — clean 3px cobalt vertical tick bar beside headings
 *   Density:      Comfortable — professional readability, clear divisions
 *   Typography:   Inter sans-serif primary & secondary (the Silicon Valley standard)
 *   Audience:     Product Managers, Program Managers, Product Engineers, Tech Leads
 *
 * Visual Signature:
 *   - Sidebar LEFT layout containing summary, technical skills, and credentials
 *   - Main column dedicated to metrics-driven professional experience and key initiatives
 *   - Experience uses the impact variant, bolding the first 3 words of every bullet point
 *   - Section wrappers use the vertical left-bar divider to direct the reader's eye downwards
 *   - Vibrant cobalt blue (#2563EB) accents
 */

export const modern_blue = {
  id: 'modern-blue',
  version: 3,
  renderer: 'enterprise',
  name: 'Product Manager',
  layout: 'sidebar-left',
  theme: 'classic-navy',
  density: 'comfortable',
  sectionDividerStyle: 'left-bar',
  sectionOrder: ['personal-info', 'summary', 'skills', 'experience', 'projects', 'education', 'certifications'],
  components: {
    header: 'header-split',
    experience: 'experience-impact',
    projects: 'projects-list',
    skills: 'skills-categorized',
    education: 'education-sidebar',
  },
  layouts: {
    'sidebar-left': {
      component: 'sidebar-left',
      slots: {
        header: ['personal-info'],
        sidebar: ['summary', 'skills', 'education', 'certifications'],
        main: ['experience', 'projects'],
      },
      overrides: {
        '--font-family-primary': '"Inter", "Helvetica Neue", Arial, sans-serif',
        '--font-family-secondary': '"Inter", "Helvetica Neue", Arial, sans-serif',
        '--color-primary': '#2563EB', // Cobalt Blue
        '--color-secondary': '#1E40AF',
        '--color-accent': '#3B82F6',
        '--color-text-main': '#1F2937',
        '--color-text-light': '#4B5563',
        '--color-border': '#E5E7EB',
        '--color-bg-page': '#FFFFFF',
        '--layout-sidebar-width': '220px',
        '--layout-column-gap': '20px',
        '--layout-sidebar-bg': '#F8FAFC', // Slate soft tint
        '--layout-sidebar-padding': '12px',
        '--layout-sidebar-border': '1px solid #E2E8F0',
        '--spacing-margin': '24px',
        '--spacing-section-mb': '14px',
        '--spacing-item-mb': '10px',
        '--spacing-bullet-gap': '4px',
      },
    },
  },
  styleOverrides: {
    '--font-size-display': '24pt',
    '--weight-display': '800',
    '--heading-weight': '700',
    '--heading-text-transform': 'uppercase',
    '--heading-letter-spacing': '0.06em',
    '--border-thickness-heading': '0',
    '--bullet-style': 'disc',
    '--font-size-heading': '9pt',
    '--font-size-body': '9pt',
    '--font-size-bullet': '8.8pt',
    '--line-height-body': '1.5',
  },
};

export default modern_blue;
